import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { chromeArgs } from '../validation.ts';
import {
  domError,
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

function normalizeError(error: unknown): RawData {
  const detail = domError(error);
  return { name: detail.name, message: detail.message };
}

async function collect(): Promise<RawData> {
  if (!navigator.mediaDevices?.enumerateDevices) {
    throw new Error('Media device enumeration is unavailable');
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      devices: devices.map((device) => ({
        kind: device.kind,
        label: device.label,
      })),
      supportedConstraints: navigator.mediaDevices.getSupportedConstraints(),
    };
  } catch (error) {
    return {
      supportedConstraints: navigator.mediaDevices.getSupportedConstraints(),
      enumerateDevicesError: normalizeError(error),
    };
  }
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return `${Array.isArray(rawData.devices) ? rawData.devices.length : 0} devices`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validateMediaDevices(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  if (!Array.isArray(rawData.devices)) {
    reasons.push('Media device enumeration failed');
    return validation(reasons);
  }
  if (!isRecord(rawData.supportedConstraints)) {
    reasons.push('Media supported constraints have an invalid shape');
  }
  for (const device of rawData.devices) {
    if (
      !isRecord(device) ||
      !['audioinput', 'videoinput', 'audiooutput'].includes(
        String(device.kind),
      ) ||
      typeof device.label != 'string'
    ) {
      reasons.push('Media device has an invalid kind or label');
      break;
    }
  }
  const args = chromeArgs(context);
  if (args?.includes('--use-fake-device-for-media-stream')) {
    const expectedDevices = [
      { kind: 'audioinput', label: '' },
      { kind: 'videoinput', label: '' },
      { kind: 'audiooutput', label: '' },
    ];
    if (JSON.stringify(rawData.devices) != JSON.stringify(expectedDevices)) {
      reasons.push(
        'Media devices do not match --use-fake-device-for-media-stream',
      );
    }
  }
  return validation(reasons);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateMediaDevices(rawData, context);
}

export const mediaDevicesTest: TestDefinition = {
  id: 'mediaDevices',
  title: 'Media devices',
  category: 'stableProfile',
  spoofingChromeArgs: ['use-fake-device-for-media-stream'],
  format,
  run,
  validate,
};
